/**
 * 2024–2026 Big Ten Football Championship Game Tiebreaker
 * (procedure published Aug. 2024; unchanged through the 2025 season)
 */

const EPS = 1e-9;
const nearlyEqual = (a, b) => a === b || Math.abs(a - b) < EPS;

const resolveBigTenTies = (tiedTeams, confTeams, games, options = {}) => {
  
  
  if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];
      

  return resolveGroup(tiedTeams, confTeams, games, options);
};

// Dispatches a group of any size to the right procedure (base cases + 3+ engine).
const resolveGroup = (group, confTeams, games, options = {}) => {
  if (group.length === 0) return [];
  if (group.length === 1) return group;
  if (group.length === 2) {

    return resolveTwoTeamTieBigTen(group[0], group[1], confTeams, games, options);
  }
  

  return resolveBigTenTie(group, confTeams, games, options);
};

// Given a scored list of remaining teams, find the top-scoring subgroup and
// the rest, then recurse per the "revert to beginning of applicable
// procedure" rule. Returns null if every team is still tied (no separation).
const splitOnScores = (remaining, scoreOf, confTeams, games, options) => {
  const scores = remaining.map(t => scoreOf(t));
  const maxScore = Math.max(...scores);

  

  const topGroup = remaining.filter((t, i) => nearlyEqual(scores[i], maxScore));
  const restGroup = remaining.filter((t, i) => !nearlyEqual(scores[i], maxScore));

  if (topGroup.length === 0 || topGroup.length === remaining.length) return null;

  const topOrdered = resolveGroup(topGroup, confTeams, games, options);
  const restOrdered = resolveGroup(restGroup, confTeams, games, options);

  return [...topOrdered, ...restOrdered];
};

const resolveBigTenTie = (tiedTeams, confTeams, games, options = {}) => {
  const { ssarankings = null, rng = Math.random } = options;
  let remaining = [...tiedTeams];

  // ---- Step 1: winning pct in games among the tied teams ----
  const h2hMap = remaining.map(t => {
    const headGames = games.filter(
      g =>
        !g.exempt &&
        (g.homeTeam === t.team || g.awayTeam === t.team) &&
        remaining.some(r => r.team !== t.team && (g.homeTeam === r.team || g.awayTeam === r.team))
    );
    const wins = headGames.filter(g => g.winner === t.team).length;
    const total = headGames.length;
    const pct = total > 0 ? wins / total : null;
    return { teamObj: t, pct, wins, total };
  });


  const allPlayedEachOther = h2hMap.every(m => m.pct !== null);

  if (allPlayedEachOther) {
    // Full round robin among the tied teams: rank directly by pct within the group.
    const split = splitOnScores(remaining, t => h2hMap.find(m => m.teamObj.team === t.team).pct, confTeams, games, options);
    if (split) return split;
    // else: totally even record among the group -> fall through to step 2
  } else {
    // Rule 1(a)/(b): only applies when not all tied teams played each other.
    const defeatedAll = remaining.filter(t =>
      remaining
        .filter(o => o.team !== t.team)
        .every(other =>
          games.some(
            g =>
              !g.exempt &&
              ((g.homeTeam === t.team && g.awayTeam === other.team) ||
                (g.awayTeam === t.team && g.homeTeam === other.team)) &&
              g.winner === t.team
          )
        )
    );
    if (defeatedAll.length === 1) {
      const top = defeatedAll[0];
      const rest = remaining.filter(r => r.team !== top.team);
      return [...resolveGroup([top], confTeams, games, options), ...resolveGroup(rest, confTeams, games, options)];
    }
    // no team beat all others -> move to next step (whole group together)
  }

  // ---- Step 2: win pct vs common conference opponents (shared by all tied teams) ----
    const scoreOf = t => winPctVsCommonOpponentsMulti(t.team, remaining, games);

    const split = splitOnScores(remaining, scoreOf, confTeams, games, options);

    if (split) return split;        

  // ---- Step 3: win pct vs next-highest-placed common opponent, in standings order ----
  {
    const scoreOf = t => winPctVsNextHighestCommonOppByOrderMulti(t.team, remaining, games, confTeams);
    const split = splitOnScores(remaining, scoreOf, confTeams, games, options);
    if (split) return split;
  }

  // ---- Step 4: cumulative conference win pct of all conference opponents ----
  {
    const scoreOf = t => combinedOppWinPct(t.team, games, confTeams);
    const split = splitOnScores(remaining, scoreOf, confTeams, games, options);
    if (split) return split;
  }


  // ---- Step 5: SportSource Analytics ranking ----
  if (ssarankings) {
    const scoreOf = t => ssarankings[t.team] ?? -Infinity;
    const split = splitOnScores(remaining, scoreOf, confTeams, games, options);
    if (split) return split;
  }

  // ---- Step 6: random draw among however many teams remain tied ----
  return [...remaining].sort((a, b) => rng() - rng());
};

// ---------- Two-team tie ----------
const resolveTwoTeamTieBigTen = (A, B, confTeams, games, options = {}) => {
  const { ssarankings = null, rng = Math.random } = options;

 

  // Step A.1/A.2 collapse to: head-to-head decides if they played; otherwise skip ahead.
  const h2h = headToHead(A, B, games);

  if (h2h) return h2h;

  // Step A.3: record vs common conference opponents
  const co = twoTeamCommonOppPct(A.team, B.team, games);
  if (co !== null) return co > 0 ? [A, B] : [B, A];

  // Step A.4: record vs next-highest-placed common opponents
  const cobo = twoTeamVsNextHighestByOrder(A.team, B.team, games, confTeams);
  if (cobo !== null) return cobo > 0 ? [A, B] : [B, A];

  // Step A.5: combined conference win pct of all conference opponents
  const aCombined = combinedOppWinPct(A.team, games, confTeams);
  const bCombined = combinedOppWinPct(B.team, games, confTeams);

  if (!nearlyEqual(aCombined, bCombined)) return aCombined > bCombined ? [A, B] : [B, A];

  // Step A.6: SSA ranking
  if (ssarankings) {
    const aScore = ssarankings[A.team] ?? -Infinity;
    const bScore = ssarankings[B.team] ?? -Infinity;
    if (aScore !== bScore) return aScore > bScore ? [A, B] : [B, A];
  }

  // Step A.7: coin toss
  return rng() < 0.5 ? [A, B] : [B, A];
};

// ---------- Helper functions ----------
const headToHead = (teamA, teamB, games) => {
  const matches = games.filter(
    g =>
      ((g.homeTeam === teamA.team && g.awayTeam === teamB.team) ||
        (g.homeTeam === teamB.team && g.awayTeam === teamA.team))
  );



  if (!matches.length) return null;
  const aWins = matches.filter(m => m.winner === teamA.team).length;
  const bWins = matches.filter(m => m.winner === teamB.team).length;

  if (aWins > bWins) {
      return [teamA, teamB];
    }
  if (bWins > aWins) return [teamB, teamA];
  return null;
};

const mapTeamGames = (team, games) =>
  games
    .filter(g => !g.exempt && (g.homeTeam === team || g.awayTeam === team))
    .map(g => ({
      opponent: g.homeTeam === team ? g.awayTeam : g.homeTeam,
      won: g.winner === team,
      conferenceGame: !!g.conferenceGame
    }));

const twoTeamCommonOppPct = (Ateam, Bteam, games) => {

   
  const Amap = mapTeamGames(Ateam, games).filter(g => g.conferenceGame);
  const Bmap = mapTeamGames(Bteam, games).filter(g => g.conferenceGame);
  const Aopps = new Set(Amap.map(g => g.opponent));
  const Bopps = new Set(Bmap.map(g => g.opponent));
  const common = [...Aopps].filter(o => Bopps.has(o));
 
  


  if (!common.length) return null;

  const Agames = Amap.filter(g => common.includes(g.opponent));
  const Bgames = Bmap.filter(g => common.includes(g.opponent));
 
  const Apct = Agames.length ? Agames.filter(g => g.won).length / Agames.length : 0;
  const Bpct = Bgames.length ? Bgames.filter(g => g.won).length / Bgames.length : 0;
if(Ateam == "Indiana"){
  //console.log(Apct);
  ////console.log(Bpct);
  //console.log(common);
  }
  if (nearlyEqual(Apct, Bpct)) return null;
  return Apct > Bpct ? 1 : -1;
};

const winPctVsCommonOpponentsMulti = (team, tiedTeams, games) => {
  const opponentsByTeam = tiedTeams.map(t => ({
    team: t.team,
    opponents: mapTeamGames(t.team, games).filter(g => g.conferenceGame).map(g => g.opponent)
  }));
  const commonOpps = opponentsByTeam
    .map(t => t.opponents)
    .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);
  if (!commonOpps.length) return 0;
  const teamGames = mapTeamGames(team, games).filter(g => g.conferenceGame && commonOpps.includes(g.opponent));
  if (!teamGames.length) return 0;
  const wins = teamGames.filter(g => g.won).length;
  return wins / teamGames.length;
};

const winPctVsNextHighestCommonOppByOrderMulti = (team, tiedTeams, games, confTeams) => {
    const opponentsByTeam = tiedTeams.map(t => ({
        team: t.team,
        opponents: mapTeamGames(t.team, games).filter(g => g.conferenceGame).map(g => g.opponent)
    }));

    const commonOpps = opponentsByTeam
        .map(t => t.opponents)
        .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0].opponents || []);

    if (!commonOpps.length) return 0;

    const getConfPct = c =>
        typeof c.conferenceWins === 'number' && typeof c.conferenceGames === 'number' && c.conferenceGames > 0
            ? c.conferenceWins / c.conferenceGames
            : 0;

    // Sort common opponents from highest winning percentage to lowest
    const sortedOpps = confTeams
        .filter(c => commonOpps.includes(c.team))
        .sort((a, b) => getConfPct(b) - getConfPct(a));

    const teamGames = mapTeamGames(team, games).filter(g => g.conferenceGame);

    // Evaluate each opponent sequentially down the standings
    for (const opp of sortedOpps) {
        const gamesVsOpp = teamGames.filter(g => g.opponent === opp.team);
        if (gamesVsOpp.length > 0) {
            const wins = gamesVsOpp.filter(g => g.won).length;
            const pct = wins / gamesVsOpp.length;

            // Return the win % for the FIRST (highest placed) common opponent where results differ
            // To integrate with splitOnScores, we return a score weighted by standing order
            return pct;
        }
    }
    return 0;
};

const twoTeamVsNextHighestByOrder = (Ateam, Bteam, games, confTeams) => {
  const aPct = winPctVsNextHighestCommonOppByOrderMulti(Ateam, [{ team: Ateam }, { team: Bteam }], games, confTeams);
  const bPct = winPctVsNextHighestCommonOppByOrderMulti(Bteam, [{ team: Ateam }, { team: Bteam }], games, confTeams);
  if (nearlyEqual(aPct, bPct)) return null;
  return aPct > bPct ? 1 : -1;
};

const combinedOppWinPct = (team, games, confTeams) => {
  const opponents = mapTeamGames(team, games).filter(g => g.conferenceGame).map(g => g.opponent);
  if (!opponents.length) return 0;
  const oppPcts = opponents.map(op => {
    const record = confTeams.find(c => c.team === op);
    return record && record.conferenceGames ? record.conferenceWins / record.conferenceGames : 0;
  });
  return oppPcts.reduce((sum, p) => sum + p, 0) / oppPcts.length;
};

export { resolveBigTenTies };